import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight, FiPhone, FiUser, FiUserPlus } from 'react-icons/fi';
import { Stepper, useToast } from '../../theme/components';
import { PageHeader } from '../../theme/components/PageHeader';
import { PageButton } from '../../theme/components/PageButton';
import { profilesApi } from '../../api/endpoints/profiles';
import DateInput from '../Common/DateInput';
import './FamilyDetails.css';

const FAMILY_REQUIRED = [
    'mothersNo', 'mothersName', 'mothersDob', 'totalChildren', 'contactNumber',
];

const FamilyDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const childData = location.state?.childData || {};

    const [formData, setFormData] = useState(location.state?.familyData || {
        mothersNo: '',
        mothersName: '',
        mothersDob: '',
        totalChildren: '',
        fathersName: '',
        contactNumber: '',
    });
    const [touched, setTouched] = useState({});

    // Defensive fallback: if the edit flow reaches FamilyDetails without
    // location.state.familyData (state lost across navigations / strict-mode
    // double-mount / etc.), hydrate the family fields from the API. Without
    // this, an empty Mother's DOB submits as null via cleanData() and
    // permanently wipes family_info.mothers_dob.
    useEffect(() => {
        const childId = location.state?.childData?.id;
        const isEdit = Boolean(location.state?.isEditMode);
        if (!isEdit || !childId || location.state?.familyData) return;
        let cancelled = false;
        profilesApi.get(childId).then((data) => {
            if (cancelled) return;
            setFormData((prev) => ({
                ...prev,
                mothersNo: prev.mothersNo || data.mothersNo || '',
                mothersName: prev.mothersName || data.mothersName || '',
                mothersDob: prev.mothersDob || data.mothersDob || '',
                totalChildren: prev.totalChildren || data.totalChildren || '',
                fathersName: prev.fathersName || data.fathersName || '',
                contactNumber: prev.contactNumber || data.contactNumber || '',
            }));
        }).catch(() => { /* leave defaults if API fails */ });
        return () => { cancelled = true; };
    }, []); // ponytail: mount-only fallback, deps intentionally empty

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };
    const handleBlur = (e) => {
        setTouched((prev) => ({ ...prev, [e.target.name]: true }));
    };

    const errorFor = (field) => touched[field] && !formData[field] && 'Required';

    const handleNext = () => {
        const missing = FAMILY_REQUIRED.filter(
            (f) => !formData[f] || String(formData[f]).trim() === '',
        );
        if (missing.length) {
            const next = { ...touched };
            missing.forEach((m) => (next[m] = true));
            setTouched(next);
            toast.error('Please fill out all required fields marked with * before proceeding.');
            return;
        }
        navigate('/review-and-finish', {
            state: {
                childData,
                familyData: formData,
                originalChildData: location.state?.originalChildData,
                originalFamilyData: location.state?.originalFamilyData,
                isEditMode: location.state?.isEditMode,
            },
        });
    };

    const handleBack = () => {
        // Step 2 → Step 1: go back to Child Details with the latest
        // values intact so the user can fix the reg_no (or any other
        // child field) without losing what they entered on this step.
        navigate('/add-child', {
            state: {
                childData,
                familyData: formData,
                originalChildData: location.state?.originalChildData,
                originalFamilyData: location.state?.originalFamilyData,
                isEditMode: location.state?.isEditMode,
            },
        });
    };

    return (
        <div className="family-details-container">
            <PageHeader
                title="Add New Child"
                subtitle="Step 2 of 3 — family details"
                icon={FiUserPlus}
            />
            <div className="family-details-card">

                <div className="family-stepper-bar">
                    <Stepper steps={['Child', 'Family', 'Review']} currentStep={1} />
                </div>

                <div className="family-form">
                    <div className="form-group cr-full-width">
                        <label htmlFor="mothersNo">Mother's No <span className="required-star">*</span></label>
                        <div className={`input-with-icon ${errorFor('mothersNo') ? 'has-error' : ''}`}>
                            <FiUser className="input-icon" />
                            <input
                                id="mothersNo"
                                type="text"
                                name="mothersNo"
                                placeholder="M-12345"
                                value={formData.mothersNo}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </div>
                        {errorFor('mothersNo') && <span className="cr-error-text">{errorFor('mothersNo')}</span>}
                    </div>

                    <div className="form-group cr-full-width">
                        <label htmlFor="mothersName">Mother's Name <span className="required-star">*</span></label>
                        <div className={`input-with-icon ${errorFor('mothersName') ? 'has-error' : ''}`}>
                            <FiUser className="input-icon" />
                            <input
                                id="mothersName"
                                type="text"
                                name="mothersName"
                                placeholder="e.g. Nayana Kumari"
                                value={formData.mothersName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </div>
                        {errorFor('mothersName') && <span className="cr-error-text">{errorFor('mothersName')}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="mdob">Mother's DOB <span className="required-star">*</span></label>
                        <div className={`input-with-icon ${errorFor('mothersDob') ? 'has-error' : ''}`}>
                            <DateInput
                                id="mdob"
                                name="mothersDob"
                                value={formData.mothersDob || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                            />
                        </div>
                        {errorFor('mothersDob') && <span className="cr-error-text">{errorFor('mothersDob')}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="totalChildren">Total No. of children alive (incl. newborn) <span className="required-star">*</span></label>
                        <div className={`input-with-icon select-wrapper ${errorFor('totalChildren') ? 'has-error' : ''}`}>
                            <select
                                id="totalChildren"
                                name="totalChildren"
                                value={formData.totalChildren}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            >
                                <option value="">Select Count</option>
                                {[...Array(10)].map((_, i) => (
                                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                                ))}
                            </select>
                        </div>
                        {errorFor('totalChildren') && <span className="cr-error-text">{errorFor('totalChildren')}</span>}
                    </div>

                    <div className="form-group cr-full-width">
                        <label htmlFor="fathersName">Father's Name</label>
                        <div className="input-with-icon">
                            <FiUser className="input-icon" />
                            <input
                                id="fathersName"
                                type="text"
                                name="fathersName"
                                placeholder="e.g. Kamal Perera"
                                value={formData.fathersName}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="form-group cr-full-width">
                        <label htmlFor="contactNumber">Contact Number <span className="required-star">*</span></label>
                        <div className={`input-with-icon ${errorFor('contactNumber') ? 'has-error' : ''}`}>
                            <FiPhone className="input-icon" />
                            <input
                                id="contactNumber"
                                type="tel"
                                inputMode="tel"
                                name="contactNumber"
                                placeholder="07XXXXXXXX"
                                value={formData.contactNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </div>
                        {errorFor('contactNumber') && <span className="cr-error-text">{errorFor('contactNumber')}</span>}
                    </div>
                </div>

                <div className="family-footer">
                    <PageButton type="button" variant="ghost" startIcon={<FiArrowLeft />} onClick={handleBack}>
                        Back
                    </PageButton>
                    <PageButton type="button" variant="primary" endIcon={<FiArrowRight />} onClick={handleNext}>
                        Next: Review
                    </PageButton>
                </div>
            </div>
        </div>
    );
};

export default FamilyDetails;
